import React, { useCallback, useEffect, useState } from "react";
import {
  Affix,
  Breadcrumb,
  Button,
  Col,
  Divider,
  Form,
  Input,
  Layout,
  Row,
  Select,
  Skeleton,
  Space,
  Tag,
  Typography,
  message,
  theme,
} from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { SaveOutlined } from "@ant-design/icons";
import { WizardService } from "../../services/WizardService";
import { AppRoutes } from "../../AppRoutes";
import { Mapping } from "../../types/mapping";
import { SdkCategory } from "../../types/sdk";
import { Requirement } from "../../types/requirement";
import { ResponsiveCard } from "../components/ResponsiveCard";
import { MEDIATION_ENGINES, engineDependencyFieldNames, engineDeps } from "./mediationEngines";
import { SpmSourceCard } from "./components/SpmSourceCard";
import { adTypeTags, categoryTag, requirementTag } from "../components/ColumnBuilder";

const { Content } = Layout;
const { Text, Title } = Typography;
const { Option } = Select;
const { useToken } = theme;

const extractErrorMessage = (error: any): string =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  "Something went wrong";

/**
 * Primary Mapping editor (SDK-562 T4). Replaces the former read-only breakdown: general +
 * settings + all five per-mediation dependency pairs are edited in one `Form`, saved together
 * via a single Save button (`PATCH /v4/status/{id}`). Each mediation card additionally embeds
 * a `SpmSourceCard` which edits the mapping+engine-scoped SPM source row immediately, via
 * `v4/spm`, independent of the Mapping form's Save.
 */
export const MappingViewPage = () => {
  const api = WizardService.Mapping;
  const navigate = useNavigate();
  const { id } = useParams();
  const { token } = useToken();

  const [form] = Form.useForm();
  const [isMappingLoading, setMappingLoading] = useState<boolean>(false);
  const [isSaving, setSaving] = useState<boolean>(false);
  const [mapping, setMapping] = useState<Mapping>();
  const [isDirty, setDirty] = useState<boolean>(false);
  const [isAdTypesVisible, setAdTypesVisible] = useState<boolean>(false);

  // Live-tracks the Form's current values so the summary tags below reflect in-progress edits,
  // not just the last-saved `mapping` state.
  const watchedStatus = Form.useWatch("status", form);
  const watchedCategory = Form.useWatch("category", form);
  const watchedRequirement = Form.useWatch("requirement", form);
  const watchedAdTypes = Form.useWatch("adTypes", form);

  const fetchMapping = useCallback(() => {
    setMappingLoading(true);
    api
      .one(parseInt(id!))
      .then((loaded) => {
        setMapping(loaded);
        form.setFieldsValue(loaded);
        setAdTypesVisible(loaded.category === SdkCategory.AD_NETWORK);
        setDirty(false);
      })
      .catch((error) => message.error(extractErrorMessage(error)))
      .finally(() => setMappingLoading(false));
  }, [api, id, form]);

  useEffect(() => {
    fetchMapping();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onValuesChange = (changedValues: any) => {
    setDirty(true);
    if (changedValues.category !== undefined) {
      setAdTypesVisible(changedValues.category === SdkCategory.AD_NETWORK);
    }
  };

  const handleSave = () => {
    form
      .validateFields()
      .then((values) => {
        setSaving(true);
        const updated: Mapping = { ...mapping, ...values, id: mapping!.id };
        api
          .update(mapping!.id!, updated)
          .then((saved) => {
            setMapping(saved);
            form.setFieldsValue(saved);
            setDirty(false);
            message.success("Mapping saved");
          })
          .catch((error) => message.error(extractErrorMessage(error)))
          .finally(() => setSaving(false));
      })
      .catch(() => {
        message.error("Please fix the highlighted fields before saving");
      });
  };

  return (
    <Layout>
      <Content>
        <Affix offsetTop={0}>
          <div style={{ background: token.colorBgContainer, paddingTop: "1rem" }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: "1rem" }}>
              <Breadcrumb
                items={[
                  {
                    href: "/" + AppRoutes.Wizard.MappingList,
                    title: "Mapping",
                    onClick: () => {
                      navigate(-1);
                    },
                  },
                  {
                    title: mapping?.display || "Mapping #" + id,
                  },
                ]}
              />
              <Space>
                {isDirty && <Text type="warning">Unsaved changes</Text>}
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={isSaving}
                  disabled={!isDirty || isMappingLoading}
                  onClick={handleSave}
                >
                  Save
                </Button>
              </Space>
            </Row>
            {!isMappingLoading && mapping && (
              <Row style={{ paddingBottom: "0.75rem" }}>
                <Space size={[8, 8]} wrap>
                  <Tag color="blue">{watchedStatus || mapping.status}</Tag>
                  {categoryTag(watchedCategory || mapping.category)}
                  {requirementTag(watchedRequirement || mapping.requirement)}
                  {isAdTypesVisible &&
                    adTypeTags(watchedAdTypes || mapping.adTypes)}
                </Space>
              </Row>
            )}
          </div>
        </Affix>

        {isMappingLoading || !mapping ? (
          <Skeleton active />
        ) : (
          <Form form={form} layout="vertical" onValuesChange={onValuesChange}>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <ResponsiveCard title="General" contentPadding="1rem">
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        name="status"
                        label="Status"
                        rules={[{ required: true, message: "Please input status" }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name="display"
                        label="Display Name"
                        rules={[{ required: true, message: "Please input name" }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name="category"
                        label="Category"
                        rules={[{ required: true, message: "Please select category" }]}
                      >
                        <Select>
                          <Option value={SdkCategory.AD_NETWORK}>Ad Network</Option>
                          <Option value={SdkCategory.MEDIATION}>Mediation</Option>
                          <Option value={SdkCategory.SERVICE}>Service</Option>
                          <Option value={SdkCategory.OTHER}>Other</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name="requirement"
                        label="Requirement"
                        rules={[{ required: true, message: "Please select requirement" }]}
                      >
                        <Select>
                          <Option value={Requirement.DEFAULT}>Default</Option>
                          <Option value={Requirement.OPTIONAL}>Optional</Option>
                          <Option value={Requirement.REQUIRED}>Required</Option>
                          <Option value={Requirement.DEPRECATED}>Deprecated</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    {isAdTypesVisible && (
                      <Col span={16}>
                        <Form.Item name="adTypes" label="Ad Types">
                          <Select mode="multiple">
                            <Option value="banner">Banner</Option>
                            <Option value="interstitial">Interstitial</Option>
                            <Option value="rewarded_video">Rewarded Video</Option>
                            <Option value="mrec">MREC</Option>
                            <Option value="native">Native</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                    )}
                    <Col span={24}>
                      <Form.Item name="warning" label="Warning">
                        <Input placeholder="Warning message (e.g. 'No compatible versions')" />
                      </Form.Item>
                    </Col>
                  </Row>
                </ResponsiveCard>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={24}>
                <ResponsiveCard title="Settings" contentPadding="1rem">
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="sourceIos" label="iOS Source">
                        <Input placeholder="Not set" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="sourceAndroid" label="Android Source">
                        <Input placeholder="Not set" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="ownDependencyIos" label="Own iOS Pod">
                        <Input placeholder="Not set" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="ownDependencyAndroid" label="Own Android Artifact">
                        <Input placeholder="Not set" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="skadnetwork" label="SKAdNetwork Source">
                        <Input placeholder="Not set" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="aakSource" label="AdAttributionKit Source">
                        <Input placeholder="Not set" />
                      </Form.Item>
                    </Col>
                  </Row>
                </ResponsiveCard>
              </Col>
            </Row>

            <Divider orientation="left" style={{ marginTop: "0.5rem" }}>
              <Title level={5} style={{ margin: 0 }}>
                Mediation Sources
              </Title>
            </Divider>
            <Row gutter={[16, 16]}>
              {MEDIATION_ENGINES.map((engine) => {
                const { ios, android } = engineDependencyFieldNames(engine.key);
                return (
                  <Col span={12} key={engine.key}>
                    <ResponsiveCard title={engine.label} contentPadding="1rem">
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item name={ios} label="iOS Pod / Artifact">
                            <Input placeholder="Not set" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name={android} label="Android Artifact">
                            <Input placeholder="Not set" />
                          </Form.Item>
                        </Col>
                      </Row>
                      <SpmSourceCard
                        mappingId={mapping.id!}
                        engine={engine.engine}
                        replacesPod={
                          mapping.status === engine.engine
                            ? mapping.ownDependencyIos
                            : engineDeps(mapping, engine.key).ios
                        }
                      />
                    </ResponsiveCard>
                  </Col>
                );
              })}
            </Row>
          </Form>
        )}
      </Content>
    </Layout>
  );
};
